import { BRAND } from "../lib/brand";
import type { ConsoleChrome } from "../lib/console-chrome";
import "./ConsoleSkeleton.css";

/**
 * One console-shaped placeholder for every wait between sign-in and the first
 * section's content (P-03, PUX-20): the lazy console chunk, the legal-status
 * check, the provider access read and a section chunk all show this shape,
 * so the page never flashes a full-screen "Tinkr…" or relays different
 * loaders. `full` draws the top bar and side navigation too (before the real
 * chrome exists); `content` fills the main column inside the real chrome.
 *
 * `chrome` picks which console the full variant imitates (D-F2): the provider
 * and partner consoles share the warm shell with a side navigation (only the
 * provider has the refresh row above its sections); the admin console has no
 * side navigation but a tab strip under a taller top bar.
 */
export type ConsoleSkeletonShape = "home" | "list";

export function ConsoleSkeleton({
  variant = "content",
  shape = "home",
  chrome = "provider",
  label,
  testId,
  className
}: {
  variant?: "full" | "content";
  shape?: ConsoleSkeletonShape;
  /** Only the full variant draws chrome; the content variant sits inside the real one. */
  chrome?: ConsoleChrome;
  /** Announced to assistive technology; the shapes themselves are hidden. */
  label: string;
  testId?: string;
  className?: string;
}) {
  const admin = chrome === "admin";
  const content = admin && variant === "full" ? <AdminSkeletonContent /> : <SkeletonContent shape={shape} />;
  if (variant === "content") {
    return (
      <div aria-busy="true" aria-label={label} className={"console-skel console-skel--content" + (className ? " " + className : "")} data-testid={testId} role="status">
        {content}
      </div>
    );
  }
  return (
    <div aria-busy="true" aria-label={label} className={"console-skel console-skel--full console-skel--" + chrome + (className ? " " + className : "")} data-testid={testId} role="status">
      <div aria-hidden="true" className="console-skel-top">
        <span className="console-skel-brand">
          <span className="console-skel-block console-skel-burger" />
          {/* The real logo where the top bar draws it, so the hand-over keeps
              it in place and the first frame is not blank shapes only. */}
          <img alt="" className="console-skel-logo" src={BRAND.logos.fullColorHorizontal} />
        </span>
        {admin ? (
          <span className="console-skel-actions">
            {Array.from({ length: 3 }, (_, index) => (
              <span className="console-skel-block console-skel-action" key={index} />
            ))}
          </span>
        ) : (
          <span className="console-skel-block console-skel-avatar" />
        )}
      </div>
      <div className="console-skel-body">
        {admin ? (
          <div aria-hidden="true" className="console-skel-tabstrip">
            {Array.from({ length: 8 }, (_, index) => (
              <span className="console-skel-block console-skel-tab" key={index} />
            ))}
            <span className="console-skel-block console-skel-search" />
          </div>
        ) : (
          <div aria-hidden="true" className="console-skel-nav">
            <span className="console-skel-block console-skel-navitem console-skel-navitem--short" />
            {Array.from({ length: 7 }, (_, index) => (
              <span className="console-skel-block console-skel-navitem" key={index} />
            ))}
          </div>
        )}
        <div className="console-skel-main">
          {chrome === "provider" ? <span aria-hidden="true" className="console-skel-toolbar" /> : null}
          {content}
        </div>
      </div>
    </div>
  );
}

function SkeletonContent({ shape }: { shape: ConsoleSkeletonShape }) {
  if (shape === "list") {
    return (
      <div aria-hidden="true" className="console-skel-content">
        <span className="console-skel-block console-skel-title" />
        <span className="console-skel-block console-skel-tabs" />
        {Array.from({ length: 5 }, (_, index) => (
          <span className="console-skel-block console-skel-row" key={index} />
        ))}
      </div>
    );
  }
  return (
    <div aria-hidden="true" className="console-skel-content">
      <span className="console-skel-block console-skel-title" />
      <div className="console-skel-kpis">
        {Array.from({ length: 4 }, (_, index) => (
          <span className="console-skel-block console-skel-kpi" key={index} />
        ))}
      </div>
      <div className="console-skel-grid">
        <span className="console-skel-block console-skel-card" />
        <span className="console-skel-block console-skel-rail" />
      </div>
    </div>
  );
}

/** The admin Overview's shape: sub-tabs, a heading, one wide card, a row of stats. */
function AdminSkeletonContent() {
  return (
    <div aria-hidden="true" className="console-skel-content">
      <div className="console-skel-subtabs">
        <span className="console-skel-block console-skel-subtab" />
        <span className="console-skel-block console-skel-subtab" />
      </div>
      <span className="console-skel-block console-skel-title console-skel-title--tall" />
      <span className="console-skel-block console-skel-card console-skel-card--wide" />
      <div className="console-skel-kpis">
        {Array.from({ length: 4 }, (_, index) => (
          <span className="console-skel-block console-skel-kpi" key={index} />
        ))}
      </div>
    </div>
  );
}

/** Thin top bar while a section change is pending in a transition (P-07). */
export function ConsoleNavProgress({ active, label }: { active: boolean; label: string }) {
  if (!active) return null;
  return <div aria-label={label} className="console-nav-progress" role="progressbar" />;
}
